import React from 'react';
import { Link } from 'react-router-dom';

const RecipesPage: React.FC = () => {
  const recipes = [
    {
      id: 'ghormeh-sabzi',
      name: 'Ghormeh Sabzi',
      persianName: 'قورمه سبزی',
      description: 'A fragrant herb stew with tender lamb, kidney beans, and dried limes',
      difficulty: 'medium',
      cookingTime: '2 hours',
      servings: 6,
      calories: 320,
      image: '🌿',
      ingredients: ['lamb', 'herbs', 'kidney beans', 'dried limes', 'onions'],
      tags: ['stew', 'herbs', 'traditional']
    },
    {
      id: 'fesenjan',
      name: 'Fesenjan',
      persianName: 'فسنجان',
      description: 'Rich pomegranate and walnut stew with tender chicken or duck',
      difficulty: 'hard',
      cookingTime: '3 hours',
      servings: 4,
      calories: 450,
      image: '🍗',
      ingredients: ['chicken', 'pomegranate', 'walnuts', 'onions', 'spices'],
      tags: ['stew', 'pomegranate', 'luxury']
    },
    {
      id: 'chelo-kabab',
      name: 'Chelo Kabab',
      persianName: 'چلو کباب',
      description: 'Grilled meat kebabs served with saffron rice and grilled tomatoes',
      difficulty: 'medium',
      cookingTime: '1.5 hours',
      servings: 4,
      calories: 380,
      image: '🍖',
      ingredients: ['lamb', 'saffron rice', 'tomatoes', 'onions', 'spices'],
      tags: ['kebab', 'rice', 'grilled']
    },
    {
      id: 'tahchin',
      name: 'Tahchin',
      persianName: 'ته چین',
      description: 'Layered saffron rice cake with chicken, yogurt, and barberries',
      difficulty: 'hard',
      cookingTime: '2.5 hours',
      servings: 6,
      calories: 420,
      image: '🍚',
      ingredients: ['chicken', 'saffron rice', 'yogurt', 'barberries', 'eggs'],
      tags: ['rice', 'layered', 'festive']
    },
    {
      id: 'ash-reshteh',
      name: 'Ash Reshteh',
      persianName: 'آش رشته',
      description: 'Hearty noodle soup with beans, herbs, and kashk (whey)',
      difficulty: 'easy',
      cookingTime: '1 hour',
      servings: 8,
      calories: 280,
      image: '🍜',
      ingredients: ['noodles', 'beans', 'herbs', 'kashk', 'onions'],
      tags: ['soup', 'noodles', 'comfort']
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'difficulty-easy';
      case 'medium': return 'difficulty-medium';
      case 'hard': return 'difficulty-hard';
      default: return 'difficulty-medium';
    }
  };

  const getIngredientIcon = (ingredient: string) => {
    const iconMap: { [key: string]: string } = {
      'lamb': '🥩',
      'chicken': '🍗',
      'herbs': '🌿',
      'kidney beans': '🫘',
      'dried limes': '🍋',
      'onions': '🧅',
      'pomegranate': '🍎',
      'walnuts': '🌰',
      'spices': '🌶️',
      'saffron rice': '🍚',
      'tomatoes': '🍅',
      'yogurt': '🥛',
      'barberries': '🫐',
      'eggs': '🥚',
      'noodles': '🍜',
      'beans': '🫘',
      'kashk': '🥛'
    };
    return iconMap[ingredient] || '🥄';
  };

  return (
    <div className="min-h-screen bg-persian-bg py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-elegant text-persian-saffron mb-4">
            Persian Recipes
          </h1>
          <p className="text-xl text-persian-text-alt max-w-3xl mx-auto">
            Discover the rich flavors and cultural heritage of Persian cuisine through these traditional recipes
          </p>
        </div>

        {/* Cultural Introduction */}
        <div className="cultural-trivia mb-12">
          <h3 className="text-lg font-elegant text-persian-saffron mb-3">
            🏛️ Persian Culinary Heritage
          </h3>
          <p className="text-persian-text-alt">
            Persian cuisine is one of the world's oldest and most sophisticated culinary traditions, 
            dating back over 2,500 years. It combines the use of fresh herbs, aromatic spices, 
            and slow-cooking techniques to create dishes that are both flavorful and nutritious.
          </p>
        </div>

        {/* Recipe Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {recipes.map((recipe) => (
            <div 
              key={recipe.id}
              className={`recipe-card group ${recipe.calories < 100 ? 'low-calorie' : ''}`}
            >
              {/* Recipe Header */}
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">{recipe.image}</div>
                <h2 className="text-2xl font-elegant text-persian-saffron mb-2">
                  {recipe.name}
                </h2>
                <h3 className="text-lg text-persian-sky mb-3 font-farsi">
                  {recipe.persianName}
                </h3>
                <p className="text-persian-text-alt text-sm leading-relaxed">
                  {recipe.description}
                </p>
              </div>

              {/* Recipe Info */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="text-center p-3 bg-persian-card-alt rounded-soft">
                  <div className="cooking-time text-sm">
                    <span>{recipe.cookingTime}</span>
                  </div>
                </div>
                <div className="text-center p-3 bg-persian-card-alt rounded-soft">
                  <div className="servings text-sm">
                    <span>{recipe.servings} servings</span>
                  </div>
                </div>
              </div>

              {/* Difficulty and Calories */}
              <div className="flex justify-between items-center mb-6">
                <span className={`text-sm font-semibold ${getDifficultyColor(recipe.difficulty)}`}>
                  {recipe.difficulty.charAt(0).toUpperCase() + recipe.difficulty.slice(1)}
                </span>
                <span className="text-persian-text-alt text-sm">
                  {recipe.calories} cal
                </span>
              </div>

              {/* Ingredients Preview */}
              <div className="mb-6">
                <h4 className="section-title text-base">Key Ingredients</h4>
                <div className="grid grid-cols-2 gap-2">
                  {recipe.ingredients.slice(0, 6).map((ingredient, index) => (
                    <div 
                      key={index}
                      className="ingredient-item text-sm"
                      style={{ '--ingredient-icon': `"${getIngredientIcon(ingredient)}"` } as React.CSSProperties}
                    >
                      <span className="text-persian-sky mr-2">
                        {getIngredientIcon(ingredient)}
                      </span>
                      <span className="text-persian-text-alt capitalize">
                        {ingredient}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  {recipe.tags.map((tag, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-persian-saffron/20 text-persian-saffron rounded-full text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* View Recipe Button */}
              <Link
                to={`/cultural/recipes/${recipe.id}`}
                className="btn-persian-primary w-full text-center group-hover:animate-ingredient-bounce"
              >
                View Full Recipe
              </Link>
            </div>
          ))}
        </div>

        {/* Persian Cooking Tips */}
        <div className="mt-16">
          <div className="persian-card">
            <h3 className="text-2xl font-elegant text-persian-saffron mb-6 text-center">
              Persian Cooking Wisdom
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="nutrition-tip">
                <h4 className="text-lg font-semibold text-persian-mint mb-3">
                  🌿 The Power of Herbs
                </h4>
                <p className="text-persian-text-alt text-sm">
                  Persian cuisine relies heavily on fresh herbs like parsley, cilantro, dill, and mint. 
                  These herbs not only add flavor but also provide essential nutrients and antioxidants.
                </p>
              </div>
              <div className="nutrition-tip">
                <h4 className="text-lg font-semibold text-persian-mint mb-3">
                  ⏱️ Slow Cooking Tradition
                </h4>
                <p className="text-persian-text-alt text-sm">
                  Many Persian dishes are slow-cooked to develop deep, complex flavors. 
                  This method also makes meats more tender and allows spices to fully infuse the dish.
                </p>
              </div>
              <div className="nutrition-tip">
                <h4 className="text-lg font-semibold text-persian-mint mb-3">
                  🍚 Rice Mastery
                </h4>
                <p className="text-persian-text-alt text-sm">
                  Persian rice is often flavored with saffron and served with a crispy bottom layer called 'tahdig'. 
                  The rice is typically parboiled, then steamed with butter and saffron.
                </p>
              </div>
              <div className="nutrition-tip">
                <h4 className="text-lg font-semibold text-persian-mint mb-3">
                  🍋 Sour Elements
                </h4>
                <p className="text-persian-text-alt text-sm">
                  Dried limes, pomegranate, and sumac are commonly used to add sour notes to dishes. 
                  These ingredients help balance rich flavors and add complexity to the cuisine.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <div className="persian-card max-w-2xl mx-auto">
            <h3 className="text-2xl font-elegant text-persian-saffron mb-4">
              Ready to Cook Persian?
            </h3>
            <p className="text-persian-text-alt mb-6">
              Start with one of these traditional recipes and experience the rich flavors of Persian cuisine. 
              Each dish tells a story of culture, tradition, and culinary excellence.
            </p>
            <Link
              to="/cultural"
              className="btn-persian-secondary inline-block"
            >
              Explore More Cultural Content
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipesPage; 